import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import CustomerServices from "../../services/CustomerServices";
import { HollowDotsSpinner } from "react-epic-spinners";
import { SearchIcon } from "@heroicons/react/solid";
import { DocumentDownloadIcon } from "@heroicons/react/outline";
import DatePicker from "react-date-picker";
import Paginate from "../../utils/Paginate";
import { convert, formatDate } from "../../utils/convertDate";
import fire from "../../utils/Toastr";

export default function VerificationEmails() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [deviceID, setDeviceID] = useState("");
  const [formValues, setFormValues] = useState({ from: null, to: null });
  const [fromm, setFromm] = useState("");
  const [too, setToo] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: 10,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  });

  const pageSize = 10;

  const fetchEmails = async (page = 1, filters = {}) => {
    const searchTerm = filters.search ?? search;
    const device = filters.deviceID ?? deviceID;
    const from = filters.from ?? fromm;
    const to = filters.to ?? too;
    setLoading(true);
    try {
      const response = await CustomerServices.listVerificationEmails(
        page,
        pageSize,
        device,
        searchTerm,
        from,
        to
      );
      if (response && response.data) {
        setEmails(response.data.result || []);
        setPagination({
          pageIndex: response.data.pageIndex,
          pageSize,
          totalPages: response.data.totalPages,
          totalCount: response.data.totalCount,
          hasPreviousPage: response.data.hasPreviousPage,
          hasNextPage: response.data.hasNextPage,
        });
      }
    } catch (error) {
      console.error("Error fetching verification emails:", error);
      fire("error", "Failed to load verification emails");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmails(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, pageIndex: newPage }));
    fetchEmails(newPage);
  };

  const handleDateFilter = async (e) => {
    e.preventDefault();
    const from = formValues.from ? convert(formatDate(formValues.from)) : "";
    const to = formValues.to ? convert(formatDate(formValues.to)) : "";
    setFromm(from);
    setToo(to);
    fetchEmails(1, { from, to });
  };

  const exportCSV = async () => {
    setExportLoading(true);
    try {
      const resp = await CustomerServices.exportVerificationEmails(
        pagination.pageIndex,
        pageSize,
        deviceID,
        search,
        fromm,
        too
      );
      if (resp && resp.data) {
        window.open(resp.data);
      } else {
        fire("error", resp?.message || "Export failed");
      }
    } catch (error) {
      console.error("Error exporting verification emails:", error);
      fire("error", "Failed to export verification emails");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      <Layout
        title={"Verification Emails"}
        pageTag={"customer"}
        current={"/verification-emails"}
      >
        <main>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
            <div className="py-4">
              <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                  <h1 className="text-lg font-bold text-gray-900">
                    Verification Emails
                  </h1>
                  <p className="mt-1 text-sm text-gray-500">
                    Email verification requests sent during onboarding.
                  </p>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-end gap-3">
                <div className="relative rounded-md shadow-sm">
                  <p className="text-sm leading-3 text-gray-500">Start Date</p>
                  <DatePicker
                    onChange={(e) =>
                      setFormValues({ ...formValues, from: e })
                    }
                    value={formValues.from}
                  />
                </div>
                <div className="relative rounded-md shadow-sm">
                  <p className="text-sm leading-3 text-gray-500">End Date</p>
                  <DatePicker
                    onChange={(e) =>
                      setFormValues({ ...formValues, to: e })
                    }
                    value={formValues.to}
                  />
                </div>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SearchIcon
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </div>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search email / phone / username"
                    className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                <div className="relative rounded-md shadow-sm">
                  <input
                    type="text"
                    value={deviceID}
                    onChange={(e) => setDeviceID(e.target.value)}
                    placeholder="Device ID"
                    className="focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleDateFilter}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                >
                  Search
                </button>
                <button
                  type="button"
                  onClick={exportCSV}
                  disabled={exportLoading}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-green-100 border border-green-300 rounded-md"
                >
                  <DocumentDownloadIcon className="w-4 mr-1.5" />
                  {exportLoading ? "Exporting..." : "Export"}
                </button>
              </div>

              <div className="mt-6 relative overflow-hidden ring-1 ring-black ring-opacity-5 md:rounded-lg bg-white">
                <div className="min-w-full py-2 align-middle md:px-6 lg:px-8 bg-white">
                  {loading ? (
                    <div className="flex justify-center py-12">
                      <HollowDotsSpinner size={50} color="#4D627E" />
                    </div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-300">
                      <thead className="bg-gray-50">
                        <tr>
                          {[
                            "Email",
                            "Phone Number",
                            "BVN",
                            "NIN",
                            "User Name",
                            "Device",
                            "Created On",
                            "Actions",
                          ].map((header) => (
                            <th
                              key={header}
                              scope="col"
                              className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {emails.length === 0 ? (
                          <tr>
                            <td
                              colSpan="8"
                              className="text-center py-8 text-gray-500 text-sm"
                            >
                              No verification emails found
                            </td>
                          </tr>
                        ) : (
                          emails.map((item) => {
                            const device = [
                              item.deviceBrand,
                              item.deviceModel,
                            ]
                              .filter(Boolean)
                              .join(" ");
                            return (
                              <tr key={item.id} className="bg-gray-50">
                                <td className="whitespace-nowrap py-3 px-3 text-sm text-gray-900">
                                  {item.email || "N/A"}
                                </td>
                                <td className="whitespace-nowrap py-3 px-3 text-sm text-gray-900">
                                  {item.phoneNumber || "N/A"}
                                </td>
                                <td className="whitespace-nowrap py-3 px-3 text-sm text-gray-900">
                                  {item.bvn || "N/A"}
                                </td>
                                <td className="whitespace-nowrap py-3 px-3 text-sm text-gray-900">
                                  {item.nin || "N/A"}
                                </td>
                                <td className="whitespace-nowrap py-3 px-3 text-sm text-gray-900">
                                  {item.userName || "N/A"}
                                </td>
                                <td className="whitespace-nowrap py-3 px-3 text-sm text-gray-500">
                                  {device || "N/A"}
                                </td>
                                <td className="whitespace-nowrap py-3 px-3 text-sm text-gray-500">
                                  {item.createdOn || "N/A"}
                                </td>
                                <td className="whitespace-nowrap py-3 px-3 text-sm text-right">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      window.location.href = `/verification-emails/view?id=${item.id}`;
                                    }}
                                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100"
                                  >
                                    View
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {emails.length > 0 && !loading && (
                <div className="mt-4">
                  <Paginate
                    nextPage={pagination.hasNextPage}
                    pageIndex={pagination.pageIndex}
                    totalCount={pagination.totalCount}
                    totalPages={pagination.totalPages}
                    prevPage={pagination.hasPreviousPage}
                    setPageIndex={handlePageChange}
                  />
                </div>
              )}
            </div>
          </div>
        </main>
      </Layout>
    </>
  );
}